import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Workflow, Upload, FileCheck2, Sparkles, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { TID } from "@/constants/testIds";
import { HERO_SLIDES, HERO_BG_VIDEO } from "@/lib/media";

export default function Hero({ onBook, onAssessment, onUpload }) {
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const timerRef = useRef(null);

  const next = () => setIdx((i) => (i + 1) % HERO_SLIDES.length);
  const prev = () => setIdx((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  // Auto-rotate every 6s
  useEffect(() => {
    timerRef.current = setInterval(next, 6000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Drag swipe
  const dragX = useRef(0);
  const onPointerDown = (e) => { dragX.current = e.clientX; };
  const onPointerUp = (e) => {
    const delta = e.clientX - dragX.current;
    if (Math.abs(delta) > 60) (delta < 0 ? next : prev)();
  };

  const slide = HERO_SLIDES[idx];

  return (
    <section className="relative overflow-hidden bg-[#0A2540]">
      {/* Background video */}
      <video
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoLoaded ? "opacity-30" : "opacity-0"}`}
        autoPlay loop playsInline muted={muted}
        onCanPlay={() => setVideoLoaded(true)}
        poster={slide.fallback}
        data-testid="hero-bg-video"
      >
        <source src={HERO_BG_VIDEO} type="video/mp4" />
      </video>

      {/* Dark overlay for contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A2540]/95 via-[#0A2540]/85 to-[#0A2540]/90" />
      <div className="absolute inset-0 hero-grid opacity-[0.08] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-20 md:pb-24 grid lg:grid-cols-12 gap-10 relative">
        {/* Left: Headline + CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-6 text-white"
        >
          <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#10B981] mb-6 bg-white/5 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
            <Sparkles size={11} /> Business Transformation Consultancy
          </div>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05]">
            Transforming Businesses Through{" "}
            <span className="relative inline-block">
              <span className="relative z-10">Continuous Improvement</span>
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
                className="absolute left-0 right-0 bottom-1 h-3 bg-[#10B981]/35 origin-left z-0"
              />
            </span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-white/75 max-w-xl leading-relaxed">
            We help organizations identify process gaps, eliminate bottlenecks, reduce waste, optimize operations, create SOPs, develop KPIs, and improve business performance — backed by measurable ROI.
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <button
              data-testid={TID.hero.bookConsult}
              onClick={onBook}
              className="btn-sharp inline-flex items-center gap-2 px-6 py-3.5 bg-[#10B981] text-white text-sm font-semibold hover:bg-[#0E9F70] transition-all hover:-translate-y-0.5 shadow-lg"
            >
              Book a Consultation <ArrowRight size={16} />
            </button>
            <button
              data-testid={TID.hero.requestAssessment}
              onClick={onAssessment}
              className="btn-sharp inline-flex items-center gap-2 px-6 py-3.5 bg-white/10 border border-white/30 text-white text-sm font-semibold backdrop-blur-sm hover:bg-white/15 hover:-translate-y-0.5 transition-all"
            >
              <FileCheck2 size={16} /> Request Assessment
            </button>
            <button
              data-testid={TID.hero.uploadContract}
              onClick={onUpload}
              className="btn-sharp inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#0A2540] text-sm font-semibold hover:bg-slate-100 hover:-translate-y-0.5 transition-all"
            >
              <Upload size={16} /> Upload Contract
            </button>
          </div>

          <div className="mt-12 flex items-center gap-5 text-[10px] uppercase tracking-[0.2em] text-white/50">
            <span className="font-semibold text-[#10B981]">Trusted across</span>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <span>Manufacturing</span><span>Healthcare</span><span>Logistics</span><span>Retail</span><span>Government</span>
            </div>
          </div>
        </motion.div>

        {/* Right: Slider */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-6"
        >
          <div
            data-testid="hero-slider"
            className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-sm border border-white/10 shadow-2xl bg-black/40 select-none"
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={slide.src}
                src={slide.src}
                alt={slide.title}
                onError={(e) => { if (e.currentTarget.src !== slide.fallback) e.currentTarget.src = slide.fallback; }}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7 }}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
            </AnimatePresence>

            {/* Caption overlay */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent p-5 sm:p-7">
              <motion.div
                key={`cap-${idx}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="font-display font-semibold text-white text-lg sm:text-xl">{slide.title}</div>
                <div className="text-xs sm:text-sm text-white/70 mt-1 max-w-md">{slide.caption}</div>
              </motion.div>
            </div>

            {/* Arrows */}
            <button
              onClick={prev}
              data-testid="hero-slider-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors rounded-full"
              aria-label="Previous"
            ><ChevronLeft size={16} /></button>
            <button
              onClick={next}
              data-testid="hero-slider-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors rounded-full"
              aria-label="Next"
            ><ChevronRight size={16} /></button>

            {/* Mute */}
            <button
              onClick={() => setMuted((m) => !m)}
              className="absolute top-3 right-3 h-8 w-8 grid place-items-center bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors rounded-full"
              aria-label={muted ? "Unmute" : "Mute"}
              title="Toggle background video sound"
            >{muted ? <VolumeX size={13} /> : <Volume2 size={13} />}</button>

            {/* Dots */}
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
              {HERO_SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  data-testid={`hero-slider-dot-${i}`}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-[#10B981]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
                />
              ))}
            </div>
          </div>

          {/* Mini stats below slider */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            {[
              { v: "32%", l: "Cost Reduced" },
              { v: "2.4x", l: "Throughput" },
              { v: "98%", l: "Compliance" },
            ].map((s) => (
              <div key={s.l} className="bg-white/5 border border-white/10 backdrop-blur-sm p-3 text-center rounded-sm">
                <div className="font-display font-bold text-xl text-white tabular">{s.v}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
